import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ShellHeader from '../components/ShellHeader';
import FeaturedCarousel from '../components/FeaturedCarousel';
import HomeFeatureLinks from '../components/HomeFeatureLinks';
import { useVehicles } from '../context/VehiclesContext';
import garageImage from '../assets/garage.png';
import logo from '../assets/logo.png';

function buildBudgetOptions(vehicles, priceType) {
  const step = priceType === 'sale' ? 1000 : 50;
  const maxValue = vehicles.reduce((highest, vehicle) => {
    const vehicleValue = Number(
      priceType === 'sale' ? vehicle.salePrice : vehicle.financePrice
    ) || 0;

    return Math.max(highest, vehicleValue);
  }, 0);

  const roundedMax = Math.max(step, Math.ceil(maxValue / step) * step);
  const options = [50];
  const startingValue = step === 50 ? 100 : step;

  for (let value = startingValue; value <= roundedMax; value += step) {
    options.push(value);
  }

  return options;
}

function formatCurrencyOption(value) {
  return `£${Number(value).toLocaleString()}`;
}

function PublicHome({ authUser }) {
  const navigate = useNavigate();
  const { vehicles, loading: isLoadingVehicles } = useVehicles();
  const [search, setSearch] = useState({
    make: '',
    model: '',
    priceType: 'sale',
    minBudget: '',
    maxBudget: '',
  });

  const sourceVehicles = useMemo(() => {
    const unsoldVehicles = vehicles.filter((vehicle) => !vehicle.isSold);
    return unsoldVehicles.length > 0 ? unsoldVehicles : vehicles;
  }, [vehicles]);

  const availableMakes = useMemo(() => {
    return [...new Set(sourceVehicles.map((vehicle) => vehicle.make).filter(Boolean))]
      .sort((left, right) => left.localeCompare(right));
  }, [sourceVehicles]);

  const makeCounts = useMemo(() => {
    return sourceVehicles.reduce((counts, vehicle) => {
      if (!vehicle.make) {
        return counts;
      }

      counts[vehicle.make] = (counts[vehicle.make] || 0) + 1;
      return counts;
    }, {});
  }, [sourceVehicles]);

  const makeMatchedVehicles = useMemo(() => {
    if (!search.make) {
      return sourceVehicles;
    }

    return sourceVehicles.filter((vehicle) => vehicle.make === search.make);
  }, [search.make, sourceVehicles]);

  const availableModels = useMemo(() => {
    if (!search.make) {
      return [];
    }

    return [
      ...new Set(
        makeMatchedVehicles
          .map((vehicle) => vehicle.model)
          .filter(Boolean)
      ),
    ].sort((left, right) => left.localeCompare(right));
  }, [makeMatchedVehicles, search.make]);

  const modelCounts = useMemo(() => {
    return makeMatchedVehicles.reduce((counts, vehicle) => {
      if (!vehicle.model) {
        return counts;
      }

      counts[vehicle.model] = (counts[vehicle.model] || 0) + 1;
      return counts;
    }, {});
  }, [makeMatchedVehicles]);

  const budgetOptions = useMemo(() => {
    return buildBudgetOptions(sourceVehicles, search.priceType);
  }, [search.priceType, sourceVehicles]);

  const minBudgetOptions = useMemo(() => {
    if (!search.maxBudget) {
      return budgetOptions;
    }

    return budgetOptions.filter((option) => option <= Number(search.maxBudget));
  }, [budgetOptions, search.maxBudget]);

  const maxBudgetOptions = useMemo(() => {
    if (!search.minBudget) {
      return budgetOptions;
    }

    return budgetOptions.filter((option) => option >= Number(search.minBudget));
  }, [budgetOptions, search.minBudget]);

  const filteredVehicles = useMemo(() => {
    return sourceVehicles.filter((vehicle) => {
      if (search.make && vehicle.make !== search.make) {
        return false;
      }

      if (search.model && vehicle.model !== search.model) {
        return false;
      }

      const comparisonValue = Number(
        search.priceType === 'sale' ? vehicle.salePrice : vehicle.financePrice
      ) || 0;

      if (search.minBudget && comparisonValue < Number(search.minBudget)) {
        return false;
      }

      if (search.maxBudget && comparisonValue > Number(search.maxBudget)) {
        return false;
      }

      return true;
    });
  }, [search.make, search.maxBudget, search.minBudget, search.model, search.priceType, sourceVehicles]);

  const makeLabelCount = search.make
    ? makeCounts[search.make] || sourceVehicles.length
    : sourceVehicles.length;

  const modelLabelCount = search.model
    ? modelCounts[search.model] || makeMatchedVehicles.length
    : search.make
      ? makeMatchedVehicles.length
      : sourceVehicles.length;

  const handleSearchChange = (event) => {
    const { name, value } = event.target;

    setSearch((current) => {
      const nextSearch = { ...current, [name]: value };

      if (name === 'make' && value !== current.make) {
        nextSearch.model = '';
      }

      if (name === 'minBudget' && current.maxBudget && Number(value) > Number(current.maxBudget)) {
        nextSearch.maxBudget = '';
      }

      if (name === 'maxBudget' && current.minBudget && Number(value) < Number(current.minBudget)) {
        nextSearch.minBudget = '';
      }

      return nextSearch;
    });
  };

  const handlePriceTypeToggle = () => {
    setSearch((current) => ({
      ...current,
      priceType: current.priceType === 'sale' ? 'finance' : 'sale',
      minBudget: '',
      maxBudget: '',
    }));
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();

    const params = new URLSearchParams();

    if (search.make) {
      params.set('make', search.make);
    }

    if (search.model) {
      params.set('model', search.model);
    }

    params.set('priceType', search.priceType);

    if (search.minBudget) {
      params.set('minBudget', search.minBudget);
    }

    if (search.maxBudget) {
      params.set('maxBudget', search.maxBudget);
    }

    navigate(`/search${params.toString() ? `?${params.toString()}` : ''}`);
  };

  return (
    <div className="site-shell">
      <ShellHeader authUser={authUser} />

      <section className="hero" style={{ backgroundImage: `url(${garageImage})` }}>
        <div className="hero-layout">
          <img src={logo} alt="MJ Motors" className="hero-brand-logo" />

          <div className="hero-stack">
            <div className="hero-overlay">
              <h1>Find Your Next Approved Used Car</h1>
              <p>
                Filter the current stock by make, model, and budget, then head to the
                search page for the full results view.
              </p>

              <form className="search-panel" onSubmit={handleSearchSubmit}>
                <label className="search-field">
                  <span>{`Make (${makeLabelCount})`}</span>
                  <select name="make" value={search.make} onChange={handleSearchChange}>
                    <option value="">{`All makes (${sourceVehicles.length})`}</option>
                    {availableMakes.map((make) => (
                      <option key={make} value={make}>
                        {`${make} (${makeCounts[make] || 0})`}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="search-field">
                  <span>{`Model (${modelLabelCount})`}</span>
                  <select
                    name="model"
                    value={search.model}
                    onChange={handleSearchChange}
                    disabled={!search.make}
                  >
                    <option value="">
                      {`All models (${search.make ? makeMatchedVehicles.length : sourceVehicles.length})`}
                    </option>
                    {availableModels.map((model) => (
                      <option key={model} value={model}>
                        {`${model} (${modelCounts[model] || 0})`}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="toggle-row" role="group" aria-label="Budget mode">
                  <span className={search.priceType === 'sale' ? 'active' : ''}>Sale</span>
                  <button
                    type="button"
                    className={`switch ${search.priceType === 'finance' ? 'on' : ''}`}
                    onClick={handlePriceTypeToggle}
                    aria-label="Toggle between sale and finance"
                  >
                    <span />
                  </button>
                  <span className={search.priceType === 'finance' ? 'active' : ''}>
                    Finance
                  </span>
                </div>

                <label className="search-field">
                  <span>Minimum Budget</span>
                  <select name="minBudget" value={search.minBudget} onChange={handleSearchChange}>
                    <option value="">No minimum</option>
                    {minBudgetOptions.map((option) => (
                      <option key={`min-${option}`} value={option}>
                        {formatCurrencyOption(option)}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="search-field">
                  <span>Maximum Budget</span>
                  <select name="maxBudget" value={search.maxBudget} onChange={handleSearchChange}>
                    <option value="">No maximum</option>
                    {maxBudgetOptions.map((option) => (
                      <option key={`max-${option}`} value={option}>
                        {formatCurrencyOption(option)}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="search-actions">
                  <p className="search-summary">
                    {filteredVehicles.length === 0
                      ? 'No matching vehicles in stock'
                      : `${filteredVehicles.length} matching vehicle${filteredVehicles.length === 1 ? '' : 's'}`}
                  </p>
                  <button type="submit" className="search-submit-btn">
                    Search Stock
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      <FeaturedCarousel vehicles={sourceVehicles} isLoading={isLoadingVehicles} />

      <HomeFeatureLinks />
    </div>
  );
}

export default PublicHome;
